import { IRechargeLocationData, IRechargeOrder } from "@recharge";
import {
  IOrderReceiptResponse,
  IRechargeOrderReceiptData,
  IChargeReceiptData,
  TOrderReceiptData,
  IDiscountDetail,
  ISubscriptionDetail,
} from "@api-clients/orders";
import Decimal from "decimal.js";
import { getCountryNameInEnglish } from "@./address";
import {
  RECHARGE_PRODUCT_TO_SUBSCRIPTION_ITEM_TYPE,
} from "libs/api/customer/src/lib/types/products";
import {
  normalizeProductTitle,
} from "libs/discount-calculation/src/util/product";
import { ProductTitle } from "libs/discount-calculation/src/types/products";
import dayjs from "dayjs";
import { RefundService } from "@stripe/module";
import { isFreshExtraProduct } from "@api-clients/subscriptions";
import { isPouchSize } from "@./cat-calculations";

import {
  ChargeBillingAddress,
  ChargeShippingAddress,
  SubscriptionItemType,
  OrderFulfilment,
  OrderFulfilmentStatus,
  DiscountApplicationToDiscountCharge,
  DiscountApplication,
  DiscountCode,
  SubscriptionType,
  PrimaryModelsClient,
  RechargeLineItem,
  SubscriptionItemVariantValue,
} from "@/primary-models";
import {
  D_ZERO,
  penceToPounds,
  toCurrencyDecimalsString,
} from "@/decimal-util";

const rechargeAddressDataToString = (address: IRechargeLocationData): string => [
  address.address1,
  address.address2,
  address.city,
  address.zip,
].filter(Boolean).join(", ");

const chargeAddressToString = (address: ChargeBillingAddress | ChargeShippingAddress | null): string => {
  if (!address) { return ""; }
  return [
    address.address1,
    address.address2,
    address.city,
    getCountryNameInEnglish(address.countryCode),
    address.postcode,
  ].filter(Boolean).join(", ");
};

/**
 * Assumes that all orders in the fulfilment order have the same status.
 * Returns null if the status is not the same for all orders.
 */
const getFulfillmentStatus = (fulfilmentOrder: OrderFulfilment[]): OrderFulfilmentStatus | null => {
  const statuses = new Set(fulfilmentOrder.map((order) => order.status));
  return statuses.size === 1 ? fulfilmentOrder[0].status : null;
};

const getDiscountDetailFromRawRechargeOrder = (rechargeOrder: IRechargeOrder): IDiscountDetail[] => rechargeOrder
  .discounts.map((d) => ({
    amount: new Decimal(d.value),
    code: d.code,
  }));

const getDiscountDetailFromDb = (discountApplicationToDiscountCharge: (DiscountApplicationToDiscountCharge & {
  discountApplication: DiscountApplication & {
    discount: DiscountCode
  }
})[]): IDiscountDetail[] => discountApplicationToDiscountCharge.map((d) => ({
  amount: d.discountAmount,
  code: d.discountApplication.discount.code,
}));

const calculateItemTotalPrice = (item: RechargeLineItem): string => (
  item.unitPrice ? toCurrencyDecimalsString(item.unitPrice.times(item.quantity)) : "0.00"
);

const formatUnitPrice = (item: RechargeLineItem): string => (
  item.unitPrice ? toCurrencyDecimalsString(item.unitPrice) : "0.00"
);

const groupItemsByParentSubscription = (
  items: RechargeLineItem[],
): Record<number, RechargeLineItem[]> => items
  .filter((item) => normalizeProductTitle(item.productTitle) !== ProductTitle.DPDDeliveryFee)
  .reduce((acc, item) => {
    if (!item.parentSubscriptionId) {
      return acc;
    }
    const parentId = Number(item.parentSubscriptionId);
    if (!acc[parentId]) {
      acc[parentId] = [];
    }
    acc[parentId].push(item);
    return acc;
  }, {} as Record<number, RechargeLineItem[]>);

const buildScoopSubscription = (
  scoopSub: RechargeLineItem,
  lineItems: RechargeLineItem[],
  subscriptionType: SubscriptionType,
  itemType: SubscriptionItemType,
  includeRechargeSubscriptionId: boolean,
): ISubscriptionDetail => {
  const totalPrice = calculateItemTotalPrice(scoopSub);
  return {
    rechargeSubscriptionId: includeRechargeSubscriptionId
      ? Number(scoopSub.purchaseItemId)
      : undefined,
    type: subscriptionType,
    subscriptionCost: totalPrice,
    extrasCost: "0.00",
    totalPrice,
    items: lineItems
      .filter((item) => normalizeProductTitle(item.productTitle) !== ProductTitle.AddOnPlan)
      .map((item) => ({
        title: itemType,
        quantity: item.quantity,
        unitPrice: formatUnitPrice(item),
        totalPrice,
      })),
  };
};

const processAddOnSubscription = (lineItems: RechargeLineItem[]): ISubscriptionDetail | null => {
  const scoopPlanetSub = lineItems
    .find((item) => normalizeProductTitle(item.productTitle) === ProductTitle.PlanetScoop);

  if (scoopPlanetSub) {
    return buildScoopSubscription(
      scoopPlanetSub,
      lineItems,
      SubscriptionType.SCOOP_PLANET,
      SubscriptionItemType.SCOOP_PLANET,
      true,
    );
  }

  const scoopHealthSub = lineItems
    .find((item) => normalizeProductTitle(item.productTitle) === ProductTitle.HealthScoop);

  if (scoopHealthSub) {
    return buildScoopSubscription(
      scoopHealthSub,
      lineItems,
      SubscriptionType.SCOOP_HEALTH,
      SubscriptionItemType.SCOOP_HEALTH,
      false,
    );
  }

  return null;
};

const processFreshSubscription = async (
  db: PrimaryModelsClient,
  lineItems: RechargeLineItem[],
  parentSubscriptionId: string,
): Promise<ISubscriptionDetail | null> => {
  const freshParentSub = lineItems
    .find((item) => normalizeProductTitle(item.productTitle) === ProductTitle.KatKinFreshMealPlan
      && Number(item.purchaseItemId) === Number(parentSubscriptionId));

  if (!freshParentSub) {
    return null;
  }

  const cat = await db.cat.findFirst({
    where: {
      subscriptions: { some: { rechargeSubscriptionId: Number(parentSubscriptionId) } },
    },
  });

  const subscriptionCost = freshParentSub.unitPrice
    ? freshParentSub.unitPrice.times(freshParentSub.quantity)
    : D_ZERO;

  const totalPrice = lineItems
    .reduce((acc, item) => acc.add(item.unitPrice ? item.unitPrice.times(item.quantity) : D_ZERO), D_ZERO);

  const extrasCost = totalPrice.minus(subscriptionCost);

  const freshItems = lineItems
    .filter((item) => normalizeProductTitle(item.productTitle) !== ProductTitle.KatKinFreshMealPlan
      && normalizeProductTitle(item.productTitle) !== ProductTitle.DPDDeliveryFee)
    .map((item) => ({
      title: RECHARGE_PRODUCT_TO_SUBSCRIPTION_ITEM_TYPE[item.productTitle]
        ?? item.productTitle as SubscriptionItemType,
      quantity: item.quantity,
      unitPrice: formatUnitPrice(item),
      totalPrice: calculateItemTotalPrice(item),
      variant: item.variantTitle && isPouchSize(item.variantTitle)
        ? item.variantTitle as SubscriptionItemVariantValue
        : SubscriptionItemVariantValue.DEFAULT,
    }));

  return {
    rechargeSubscriptionId: Number(freshParentSub.purchaseItemId),
    catName: cat?.name ?? null,
    type: SubscriptionType.FRESH,
    subscriptionCost: toCurrencyDecimalsString(subscriptionCost),
    extrasCost: toCurrencyDecimalsString(extrasCost),
    totalPrice: toCurrencyDecimalsString(totalPrice),
    items: freshItems,
  };
};

const processSubscriptionGroup = async (
  db: PrimaryModelsClient,
  parentSubscriptionId: string,
  lineItems: RechargeLineItem[],
): Promise<ISubscriptionDetail | null> => {
  const parentSub = lineItems
    .find((item) => normalizeProductTitle(item.productTitle) === ProductTitle.KatKinFreshMealPlan
      || normalizeProductTitle(item.productTitle) === ProductTitle.AddOnPlan);

  if (!parentSub) {
    return null;
  }

  const normalizedTitle = normalizeProductTitle(parentSub.productTitle);

  if (normalizedTitle === ProductTitle.AddOnPlan) {
    return processAddOnSubscription(lineItems);
  }

  if (normalizedTitle === ProductTitle.KatKinFreshMealPlan) {
    return processFreshSubscription(db, lineItems, parentSubscriptionId);
  }

  return null;
};

const getSubscriptionDetailFromRechargeOrder = async (
  db: PrimaryModelsClient,
  order: IRechargeOrderReceiptData["order"],
): Promise<ISubscriptionDetail[]> => {
  const groupedItems = groupItemsByParentSubscription(order.items);

  const subscriptions = await Promise.all(
    Object.entries(groupedItems).map(
      ([ parentSubscriptionId, lineItems ]) => processSubscriptionGroup(db, parentSubscriptionId, lineItems),
    ),
  );

  return subscriptions.filter((sub): sub is ISubscriptionDetail => sub !== null);
};

const transformRechargeOrderToOrderReceipt = async (
  db: PrimaryModelsClient,
  refundService: RefundService,
  { order }: IRechargeOrderReceiptData,
): Promise<IOrderReceiptResponse> => {
  const shippingAddress = order.shippingAddress as IRechargeLocationData;
  const billingAddress = order.billingAddress as IRechargeLocationData;

  const rechargeOrder: IRechargeOrder = order.raw as unknown as IRechargeOrder;

  const refunds = order.stripeTransactionId && order.stripeTransactionId !== "FREE" ? await refundService.listRefunds({
    chargeId: order.stripeTransactionId,
  }) : [];

  const totalRefundedAmount = refunds.reduce((acc, refund) => acc.add(penceToPounds(refund.amount)), D_ZERO);

  const deliveryFee = order.items
    .filter((item) => normalizeProductTitle(item.productTitle) === "DPD Delivery Fee")
    .reduce((acc, item) => acc.add(new Decimal(item.unitPrice ?? 0).times(item.quantity)), new Decimal(0));

  const discounts = order.discountApplicationToDiscountCharge.length === 0
    ? getDiscountDetailFromRawRechargeOrder(rechargeOrder)
    : getDiscountDetailFromDb(order.discountApplicationToDiscountCharge);

  const totalPrice = order.totalPrice ?? D_ZERO;
  const refundedAmountValue = totalRefundedAmount.isNaN() || totalRefundedAmount.isZero()
    ? D_ZERO
    : totalRefundedAmount;
  const remainingBalance = new Decimal(totalPrice).minus(refundedAmountValue);

  return {
    boxIds: order.orderFulfilment.map((fulfillmentOrder) => Number(fulfillmentOrder.boxId)),
    rechargeOrderId: Number(order.id),
    katkinChargeId: null,
    rechargeChargeId: Number(order.rechargeChargeId),
    chargeDate: order.processedAt?.toISOString() ?? null,
    deliveryDate: getDeliveryDateFromFulfilment(order.orderFulfilment)
      ?? order.deliveryDate.toISOString(),
    boxNumber: order.orderFulfilment?.[0]?.subscriptionBoxNumber,
    shippingAddress: rechargeAddressDataToString(shippingAddress),
    billingAddress: rechargeAddressDataToString(billingAddress),
    totalDiscount: toCurrencyDecimalsString(new Decimal((order.raw as unknown as IRechargeOrder).total_discounts || 0)),
    tax: order.totalTax ? toCurrencyDecimalsString(order.totalTax) : "0.00",
    totalPrice: toCurrencyDecimalsString(totalPrice),
    deliveryFee: toCurrencyDecimalsString(deliveryFee),
    status: getFulfillmentStatus(order.orderFulfilment),
    refundedAmount: toCurrencyDecimalsString(refundedAmountValue),
    remainingBalance: toCurrencyDecimalsString(remainingBalance),
    subscriptions: await getSubscriptionDetailFromRechargeOrder(db, order),
    discounts,
  };
};

const getSubscriptionDetailFromCharge = async (
  db: PrimaryModelsClient,
  charge: IChargeReceiptData["charge"],
): Promise<ISubscriptionDetail[]> => Promise.all(charge.subscriptions.map(async (sub) => {

  const cat = sub.subscriptionType === SubscriptionType.FRESH
    ? (await db.cat.findFirst({
      where: {
        subscriptions: { some: { id: sub.subscriptionId } },
      },
    }))
    : undefined;

  const extrasCost = sub.items
    .filter((item) => isFreshExtraProduct(item.subscriptionItemType))
    .reduce((acc, item) => acc.add(item.originalUnitPrice.times(item.quantity)), D_ZERO);

  return {
    type: sub.subscriptionType,
    katkinSubscriptionId: sub.subscriptionId,
    catName: cat?.name ?? null,
    subscriptionCost: toCurrencyDecimalsString(sub.originalPrice),
    extrasCost: toCurrencyDecimalsString(extrasCost),
    totalPrice: toCurrencyDecimalsString(sub.originalPrice.plus(extrasCost)),
    items: sub.items.map((item) => ({
      title: item.subscriptionItemType,
      quantity: item.quantity,
      unitPrice: toCurrencyDecimalsString(item.originalUnitPrice),
      totalPrice: toCurrencyDecimalsString(item.originalUnitPrice.times(item.quantity)),
      variant: item.subscriptionItemVariantValue,
    })),
  };
}));

const transformChargeToOrderReceipt = async (
  db: PrimaryModelsClient,
  { charge }: IChargeReceiptData,
): Promise<IOrderReceiptResponse> => {
  const refundAmount = charge.refundAmount ?? D_ZERO;
  const remainingBalance = charge.totalPriceDiscounted.minus(refundAmount);

  return {
    boxIds: charge.fulfilmentOrder.map((fulfillmentOrder) => Number(fulfillmentOrder.boxId)),
    rechargeOrderId: Number(charge.rechargeOrderId),
    katkinChargeId: Number(charge.chargeId),
    rechargeChargeId: Number(charge.rechargeChargeId),
    chargeDate: charge.finalisedAt?.toISOString() ?? null,
    deliveryDate: getDeliveryDateFromFulfilment(charge.fulfilmentOrder)
    ?? charge.deliveryDate.toISOString(),
    status: getFulfillmentStatus(charge.fulfilmentOrder),
    boxNumber: Math.max(...charge.subscriptions.map((sub) => sub.orderCount)),
    billingAddress: chargeAddressToString(charge.billingAddress),
    shippingAddress: chargeAddressToString(charge.shippingAddress),
    tax: toCurrencyDecimalsString(charge.taxAmountDiscounted),
    totalPrice: toCurrencyDecimalsString(charge.totalPriceDiscounted),
    totalDiscount: toCurrencyDecimalsString(charge.totalDiscountAmount),
    deliveryFee: toCurrencyDecimalsString(charge.deliveryFeeDiscounted),
    refundedAmount: toCurrencyDecimalsString(refundAmount),
    remainingBalance: toCurrencyDecimalsString(remainingBalance),
    subscriptions: await getSubscriptionDetailFromCharge(db, charge),
    discounts: charge.discounts.map((discount) => ({
      amount: discount.discountAmount,
      code: discount.discountApplication.discount.code,
    })),
  };
};

const getDeliveryDateFromFulfilment = (fulfilmentOrder?: OrderFulfilment[]): string | null => {
  if (!fulfilmentOrder || fulfilmentOrder.length < 1) { return null; }
  const notCancelled = fulfilmentOrder.filter((it) => it.status !== OrderFulfilmentStatus.CANCELLED);
  if (notCancelled.length < 1) {
    return null;
  }
  return new Date(Math.min(...notCancelled.map((it) => it.deliveryDate.valueOf()))).toISOString();
};

export const transformOrderOrChargeToOrderReceipt = (
  db: PrimaryModelsClient,
  refundService: RefundService,
  data: TOrderReceiptData,
): Promise<IOrderReceiptResponse> => {
  if (data.old) {
    return transformRechargeOrderToOrderReceipt(db, refundService, data);
  }
  return transformChargeToOrderReceipt(db, data);
};

export const transformOrdersAndChargesToOrderReceipts = async (
  db: PrimaryModelsClient,
  refundService: RefundService,
  ordersAndCharges: TOrderReceiptData[],
): Promise<IOrderReceiptResponse[]> => {
  const receipts: IOrderReceiptResponse[] = [];
  for (const data of ordersAndCharges) {
    receipts.push(await transformOrderOrChargeToOrderReceipt(db, refundService, data));
  }
  return receipts.sort((a, b) => dayjs(b.chargeDate).diff(dayjs(a.chargeDate)));
};
